import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import {
  AdminCollapsibleContainer,
  AdminFormRow,
  AdminInput,
  AdminCheckbox,
  AdminMultiselect,
} from 'shared-components';
import { getMetadataChoices } from 'services/api/admin-products';
import { formatDescriptor } from 'utils/admin-products';
import styles from './ProductMetadataForm.module.css';
import {
  productInformationInput,
  disabledFields,
  productDesignInput,
} from './models';

const renderCheckboxes = (variantId, register, list) => {
  return _.map(list, (checkbox) => {
    const { choiceName, choiceType, choiceId } = checkbox;
    const name = `${variantId}.${_.camelCase(choiceType)}`;
    return (
      <AdminCheckbox
        label={choiceName}
        propRef={register}
        name={name}
        key={`${name}_${choiceId}`}
        value={choiceId}
        id={`${name}_${choiceId}`}
      />
    );
  });
};

const renderMultiselect = (
  watch,
  metadataChoices,
  hash,
  register,
  variantId,
  inputs,
  setValue,
  colorHexcodes
) =>
  _.map(inputs, (input) => {
    const { inputType, label, name, style, multiselectType } = input;
    const formName = `${variantId}.${name}`;
    switch (inputType) {
      case 'multiselect': {
        return (
          <AdminMultiselect
            label={label}
            style={style}
            choices={metadataChoices[name]}
            name={formName}
            selected={watch(formName)}
            hash={hash}
            type={multiselectType}
            propRef={register}
            key={formName}
            colorHexcodes={name === 'colors' ? colorHexcodes : null}
            setValue={setValue}
          />
        );
      }
      case 'input': {
        return (
          <AdminInput
            label={label}
            style={style}
            name={formName}
            type="text"
            propRef={register}
            key={formName}
          />
        );
      }
      default: {
        return <div />;
      }
    }
  });

const ProductMetadataForm = ({
  productFormData,
  reset,
  initialMetadataMap,
  register,
  watch,
  setValue,
  metadataFormData,
}) => {
  const { productCatalogVariants } = productFormData;
  const [metadataChoices, setMetadataChoices] = useState({});
  const [metadataHash, setMetadataHash] = useState({});
  const [colorHexcodes, setColorHexcodes] = useState({});

  useEffect(() => {
    const fetchMetadataOptions = async () => {
      const response = await getMetadataChoices();
      const { metadataChoices, colorHexcodes } = response;
      setColorHexcodes(colorHexcodes);
      const formattedValues = {};
      _.forOwn(metadataChoices, (choice) => {
        const { parentChoiceId } = choice;
        if (parentChoiceId) {
          // if the element is not at root level, add it to its parent array of children
          if (!metadataChoices[parentChoiceId].children)
            metadataChoices[parentChoiceId].children = [];
          metadataChoices[parentChoiceId].children.push(choice);
        } else {
          const { choiceType } = choice;
          if (!formattedValues[choiceType]) formattedValues[choiceType] = [];
          formattedValues[choiceType].push(choice);
        }
      });
      setMetadataHash(metadataChoices);
      setMetadataChoices(formattedValues);
    };
    fetchMetadataOptions();
  }, []);

  //format admin selected fields from backend to display in form
  useEffect(() => {
    if (metadataFormData) {
      reset(metadataFormData);
    } else {
      const formatted = _.reduce(
        initialMetadataMap,
        (result, value, key) => {
          result[key] = {};
          _.forEach(value, (v) => {
            const { choiceIds, choiceType, choiceValue } = v;
            result[key][choiceType] =
              choiceValue || _.map(choiceIds, (choice) => _.toString(choice));
          });
          return result;
        },
        {}
      );
      reset(formatted);
    }
  }, [initialMetadataMap, reset, metadataFormData]);

  return (
    <div>
      <h4>Individual Variable Information</h4>
      {_.map(productCatalogVariants, (variant, index) => {
        const { imageUrl, id } = variant;
        return (
          <React.Fragment key={index}>
            <AdminCollapsibleContainer
              title={`Variable Product: ${formatDescriptor(variant)}`}
            >
              <div className={styles.inactiveContainer}>
                {_.isEmpty(imageUrl) ? (
                  <div className={styles.image}>
                    <i className="fal fa-image" />
                  </div>
                ) : (
                  <img src={imageUrl} alt="variant img" />
                )}

                <div className={styles.inactiveRows}>
                  {_.map(disabledFields, (field) => {
                    const { style, name, label } = field;
                    return (
                      <AdminInput
                        type="text"
                        disabled
                        value={variant[name] || productFormData[name]}
                        label={label}
                        style={style}
                        key={`${id}_${label}`}
                      />
                    );
                  })}
                </div>
              </div>
              <hr className={styles.divider} />
              <h4>Product Attributes</h4>
              <div className={styles.inputContainer}>
                {renderMultiselect(
                  watch,
                  metadataChoices,
                  metadataHash,
                  register,
                  variant.id,
                  productInformationInput,
                  setValue
                )}
              </div>
              <AdminFormRow>
                {renderCheckboxes(
                  variant.id,
                  register,
                  metadataChoices.productCallouts
                )}
              </AdminFormRow>
              <h4 className={styles.subtitle}>Product Design</h4>
              <AdminFormRow>
                {renderMultiselect(
                  watch,
                  metadataChoices,
                  metadataHash,
                  register,
                  variant.id,
                  productDesignInput,
                  setValue,
                  colorHexcodes
                )}
              </AdminFormRow>
              <h4 className={styles.subtitle}>Ingredient Callouts</h4>
              <div className={styles.calloutsContainer}>
                {renderCheckboxes(
                  variant.id,
                  register,
                  metadataChoices.ingredientCallouts
                )}
              </div>
            </AdminCollapsibleContainer>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default ProductMetadataForm;
